import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Bot, Calendar, Clock, ChevronLeft, Check, Phone, User, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_QUESTIONS = [
  "Quel type d'enseigne pour mon commerce ?",
  "Quels sont vos prix ?",
  "Quel est le délai ?",
];

// Steps: "chat" | "collect_info" | "pick_date" | "pick_time" | "confirm" | "done"

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Bonjour ! Je suis l'expert enseignes de PUB PRO. Quel est votre projet ? Je peux vous conseiller sur le type d'enseigne, les matériaux, les prix... 💡" }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Booking state
  const [bookingStep, setBookingStep] = useState("chat"); // chat, collect_info, pick_date, pick_time, confirm, done
  const [bookingData, setBookingData] = useState({ name: "", phone: "", email: "", date: "", time: "", notes: "" });
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, bookingStep]);

  useEffect(() => {
    if (isOpen && inputRef.current && bookingStep === "chat") inputRef.current.focus();
  }, [isOpen, bookingStep]);

  const addBotMessage = useCallback((text) => {
    setMessages(prev => [...prev, { role: "bot", text }]);
  }, []);

  const startBookingFlow = useCallback(() => {
    addBotMessage("Parfait ! Je vous réserve un créneau avec notre expert. Commençons par vos coordonnées 👇");
    setBookingStep("collect_info");
  }, [addBotMessage]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMessage }]);
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, { message: userMessage, session_id: sessionId });
      const botResponse = res.data.response;

      if (botResponse.includes("[PROPOSE_RDV]")) {
        startBookingFlow();
      } else {
        setMessages(prev => [...prev, { role: "bot", text: botResponse }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: "Désolé, un souci technique. Appelez-nous au 06 43 69 36 88 !" }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    if (!bookingData.name || !bookingData.phone) return;
    addBotMessage(`Merci ${bookingData.name} ! Choisissez maintenant une date pour votre appel 📅`);
    setBookingStep("pick_date");
    try {
      const res = await axios.get(`${API}/appointments/available-dates`);
      setAvailableDates(res.data.dates);
    } catch {
      addBotMessage("Erreur lors du chargement des dates. Réessayez.");
      setBookingStep("collect_info");
    }
  };

  const handleDatePick = async (date) => {
    setBookingData(prev => ({ ...prev, date }));
    setSlotsLoading(true);
    try {
      const res = await axios.get(`${API}/appointments/slots?date=${date}`);
      setAvailableSlots(res.data.slots);
      if (res.data.slots.length === 0) {
        addBotMessage("Aucun créneau disponible ce jour. Choisissez une autre date.");
        return;
      }
      const dt = new Date(date + "T00:00:00");
      const jours = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
      addBotMessage(`${jours[dt.getDay()]} ${dt.getDate()}/${dt.getMonth() + 1} — Choisissez votre créneau 🕐`);
      setBookingStep("pick_time");
    } catch {
      addBotMessage("Erreur lors du chargement des créneaux.");
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleTimePick = (time) => {
    setBookingData(prev => ({ ...prev, time }));
    setBookingStep("confirm");
  };

  const confirmBooking = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/appointments/book`, {
        ...bookingData,
        session_id: sessionId
      });
      setBookingResult(res.data);
      addBotMessage(`✅ C'est confirmé ! Notre expert vous appellera le ${res.data.date_display} à ${res.data.time}. Un email de confirmation a été envoyé. À très bientôt !`);
      setBookingStep("done");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de la réservation. Réessayez.";
      addBotMessage(msg);
      setBookingStep("pick_time");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateLabel = (dateStr) => {
    const dt = new Date(dateStr + "T00:00:00");
    const jours = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
    const mois = ["jan", "fév", "mar", "avr", "mai", "jun", "jul", "aoû", "sep", "oct", "nov", "déc"];
    return `${jours[dt.getDay()]} ${dt.getDate()} ${mois[dt.getMonth()]}`;
  };

  const triggerBooking = () => {
    setMessages(prev => [...prev, { role: "user", text: "Je veux réserver un créneau téléphonique" }]);
    startBookingFlow();
  };

  return (
    <>
      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 md:bottom-24 right-4 md:right-5 z-40 bg-[#00FF66] text-black w-14 h-14 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(0,255,102,0.3)] hover:scale-110 hover:shadow-[0_0_30px_rgba(0,255,102,0.5)] transition-all"
          data-testid="chatbot-toggle-btn"
          aria-label="Ouvrir le chat expert"
        >
          <Bot className="w-7 h-7" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 md:bottom-24 right-4 md:right-5 z-50 w-[380px] max-w-[calc(100vw-32px)] bg-[#0A0A0A] border border-[#1a1a1a] shadow-2xl flex flex-col overflow-hidden" style={{ height: "520px" }} data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-[#111] border-b border-[#1a1a1a] px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#00FF66] rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5 text-black" />
              </div>
              <div>
                <p className="text-white font-bold text-sm font-['Outfit',sans-serif]">Expert Enseignes PUB PRO</p>
                <p className="text-[#00FF66] text-xs flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full animate-pulse" /> En ligne
                </p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-zinc-600 hover:text-white transition-colors" data-testid="chatbot-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chatbot-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-[#00FF66] text-black rounded-2xl rounded-br-sm"
                    : "bg-[#151515] text-zinc-300 rounded-2xl rounded-bl-sm border border-[#1a1a1a]"
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-[#151515] text-zinc-500 px-4 py-3 rounded-2xl rounded-bl-sm border border-[#1a1a1a] text-sm">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-[#00FF66] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-[#00FF66] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-[#00FF66] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Booking Flow UI */}
          {bookingStep === "collect_info" && (
            <div className="shrink-0 border-t border-[#1a1a1a] bg-[#0a0a0a] p-4">
              <form onSubmit={handleInfoSubmit} className="space-y-3">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <input type="text" required placeholder="Votre nom *" value={bookingData.name}
                    onChange={e => setBookingData(p => ({ ...p, name: e.target.value }))}
                    className="w-full bg-[#151515] border border-[#1a1a1a] text-white pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#00FF66]/50 placeholder-zinc-600"
                    data-testid="booking-name" />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <input type="tel" required placeholder="Téléphone *" value={bookingData.phone}
                    onChange={e => setBookingData(p => ({ ...p, phone: e.target.value }))}
                    className="w-full bg-[#151515] border border-[#1a1a1a] text-white pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#00FF66]/50 placeholder-zinc-600"
                    data-testid="booking-phone" />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <input type="email" placeholder="Email (optionnel)" value={bookingData.email}
                    onChange={e => setBookingData(p => ({ ...p, email: e.target.value }))}
                    className="w-full bg-[#151515] border border-[#1a1a1a] text-white pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#00FF66]/50 placeholder-zinc-600"
                    data-testid="booking-email" />
                </div>
                <button type="submit"
                  className="w-full bg-[#00FF66] text-black py-3 font-bold text-sm uppercase tracking-wider hover:shadow-[0_0_20px_rgba(0,255,102,0.3)] transition-all"
                  data-testid="booking-next-btn">
                  Choisir un créneau →
                </button>
              </form>
            </div>
          )}

          {bookingStep === "pick_date" && (
            <div className="shrink-0 border-t border-[#1a1a1a] bg-[#0a0a0a] p-4 max-h-[200px] overflow-y-auto">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-[#00FF66]" />
                <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Choisissez une date</span>
              </div>
              {availableDates.length === 0 ? (
                <p className="text-zinc-500 text-sm">Chargement...</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {availableDates.map(date => (
                    <button key={date} onClick={() => handleDatePick(date)}
                      className="bg-[#151515] border border-[#1a1a1a] text-white py-2.5 px-2 text-xs font-semibold hover:border-[#00FF66] hover:bg-[#00FF66]/5 transition-all"
                      data-testid={`date-${date}`}>
                      {formatDateLabel(date)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {bookingStep === "pick_time" && (
            <div className="shrink-0 border-t border-[#1a1a1a] bg-[#0a0a0a] p-4 max-h-[200px] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#00FF66]" />
                  <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Créneau</span>
                </div>
                <button onClick={() => setBookingStep("pick_date")} className="text-xs text-zinc-500 hover:text-white flex items-center gap-1">
                  <ChevronLeft className="w-3 h-3" /> Autre date
                </button>
              </div>
              {slotsLoading ? (
                <p className="text-zinc-500 text-sm">Chargement...</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {availableSlots.map(slot => (
                    <button key={slot} onClick={() => handleTimePick(slot)}
                      className="bg-[#151515] border border-[#1a1a1a] text-white py-2 text-xs font-semibold hover:border-[#00FF66] hover:bg-[#00FF66]/5 transition-all"
                      data-testid={`slot-${slot}`}>
                      {slot}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {bookingStep === "confirm" && (
            <div className="shrink-0 border-t border-[#1a1a1a] bg-[#0a0a0a] p-4">
              <div className="bg-[#151515] border border-[#1a1a1a] p-4 mb-3">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Récapitulatif</p>
                <p className="text-white font-bold text-sm">{bookingData.name}</p>
                <p className="text-zinc-400 text-xs mt-1">📞 {bookingData.phone}</p>
                <p className="text-[#00FF66] font-bold text-sm mt-2">
                  📅 {formatDateLabel(bookingData.date)} — 🕐 {bookingData.time}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setBookingStep("pick_time")}
                  className="flex-1 border border-[#333] text-white py-3 text-xs font-bold uppercase hover:bg-[#151515] transition-colors"
                  data-testid="booking-back-btn">
                  Modifier
                </button>
                <button onClick={confirmBooking} disabled={isLoading}
                  className="flex-1 bg-[#00FF66] text-black py-3 text-xs font-bold uppercase hover:shadow-[0_0_20px_rgba(0,255,102,0.3)] transition-all disabled:opacity-50"
                  data-testid="booking-confirm-btn">
                  {isLoading ? "Réservation..." : "Confirmer ✓"}
                </button>
              </div>
            </div>
          )}

          {bookingStep === "done" && (
            <div className="shrink-0 border-t border-[#1a1a1a] bg-[#0a0a0a] p-4 text-center">
              <div className="w-12 h-12 mx-auto bg-[#00FF66]/10 border border-[#00FF66]/30 rounded-full flex items-center justify-center mb-3">
                <Check className="w-6 h-6 text-[#00FF66]" />
              </div>
              <p className="text-white font-bold text-sm">Rendez-vous confirmé !</p>
              <p className="text-zinc-500 text-xs mt-1">Vérifiez votre boîte email</p>
              <button onClick={() => { setBookingStep("chat"); }}
                className="mt-3 text-[#00FF66] text-xs font-bold underline underline-offset-2 hover:text-white transition-colors"
                data-testid="booking-new-question">
                Poser une autre question
              </button>
            </div>
          )}

          {/* Quick actions + RDV button (shown during chat) */}
          {bookingStep === "chat" && (
            <>
              {messages.length <= 2 && (
                <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
                  {QUICK_QUESTIONS.map((q) => (
                    <button key={q} onClick={() => setInput(q)}
                      className="text-xs bg-[#151515] text-[#00FF66] border border-[#1a1a1a] px-3 py-1.5 rounded-full hover:bg-[#1a1a1a] transition-colors"
                      data-testid={`chatbot-quick-${q.slice(0, 10)}`}>
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Always show RDV button during chat */}
              <div className="px-4 pb-2 shrink-0">
                <button onClick={triggerBooking}
                  className="w-full flex items-center justify-center gap-2 bg-[#111] border border-[#00FF66]/30 text-[#00FF66] py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#00FF66]/5 transition-all"
                  data-testid="booking-trigger-btn">
                  <Calendar className="w-3.5 h-3.5" />
                  Réserver un appel gratuit avec notre expert
                </button>
              </div>

              {/* Input */}
              <div className="border-t border-[#1a1a1a] px-3 py-3 bg-[#0A0A0A] shrink-0">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Posez votre question..."
                    className="flex-1 bg-[#151515] border border-[#1a1a1a] text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#00FF66]/30 placeholder-zinc-600"
                    data-testid="chatbot-input"
                    disabled={isLoading}
                  />
                  <button onClick={sendMessage} disabled={!input.trim() || isLoading}
                    className="bg-[#00FF66] text-black w-10 h-10 flex items-center justify-center hover:shadow-[0_0_15px_rgba(0,255,102,0.3)] transition-all disabled:opacity-30"
                    data-testid="chatbot-send-btn">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
